/**
 * Plain words for the pack-lot, release and lab screens (F7.4–F7.6, F7-B1.a): checklist lines,
 * server detail lines, hold reasons, decision kinds and evidence links.
 */
import { EXCEPTION_LABEL, humanize, statusLabel, subjectLabel } from '../../labels'

export const CHECKLIST_KEYS = [
  'lab_pass',
  'label_ok',
  'pouch_scans_ok',
  'count_ok',
  'upstream_balanced',
  'storage_declared',
  'upstream_clear',
  'exceptions_closed',
] as const

/** Each release checklist line: its name and what it checks. */
export const CHECKLIST_LABEL: Record<string, { label: string; hint: string }> = {
  lab_pass: {
    label: 'Lab result',
    hint: "Pass on every required test, sensitive enough for the label's claim, from an accredited lab, recent enough.",
  },
  label_ok: { label: 'Label', hint: 'The approved label version was used and the last pouch matched it.' },
  pouch_scans_ok: { label: 'Pouch scans', hint: 'The first and last pouch QR codes matched this pack lot.' },
  count_ok: { label: 'Counts', hint: 'Codes printed = applied + spoiled + destroyed, and pouches = labels applied.' },
  upstream_balanced: {
    label: 'Weight balance',
    hint: "This pack run and every machine run back to the farmer's lot balanced, and no more was packed than he delivered.",
  },
  storage_declared: {
    label: 'Storage',
    hint: 'Every sack had a known place from the gate on, in stores with a valid no-fumigant declaration.',
  },
  upstream_clear: { label: 'Holds upstream', hint: 'No lot or batch this pack lot came from is on hold.' },
  exceptions_closed: {
    label: 'Open problems',
    hint: 'No open problem on the pack lot, its run, or anything it came from.',
  },
}

export const checklistLabel = (key: string) => CHECKLIST_LABEL[key]?.label ?? humanize(key)

/** Short words kept as written (units and acronyms). */
const KEEP = new Set(['LOQ', 'MRP', 'QR', 'PDF', 'ID', 'KG', 'FSSAI', 'GTIN', 'QC'])
/** Words that read better in a sentence than their status label. */
const IN_SENTENCE: Record<string, string> = {
  HELD: 'on hold',
  RECALLED: 'recalled',
  QUARANTINE: 'on hold',
  EXCEPTION: 'out of balance',
}
const SUBJECTS = new Set(['RUN', 'LOT', 'UNIT', 'BATCH', 'PACK_LOT', 'SACK', 'BAG', 'SAMPLE', 'SHIPMENT'])

/**
 * A server detail line, readable: "Run PR-1 is EXCEPTION with 2 open problem(s)" → "… is Needs a
 * decision …"; "BALANCE_OUT_OF_BAND open on RUN" → "Balance out of band open on run". Codes with
 * digits or dashes (run, lot, report numbers) are left alone.
 */
export function readableDetail(line: string): string {
  return line.replace(/(^|[\s(])([A-Z][A-Z_]{1,})(?=$|[\s.,:;)])/g, (_m, pre: string, tok: string) => {
    if (KEEP.has(tok)) return pre + tok
    if (IN_SENTENCE[tok]) return pre + IN_SENTENCE[tok]
    if (tok.length <= 2) return pre + tok
    const ex = EXCEPTION_LABEL[tok]
    if (ex) return pre + ex.en
    if (SUBJECTS.has(tok)) return pre + subjectLabel(tok).toLowerCase()
    const word = statusLabel(tok).en
    // Mid-sentence words read lower case ("Lab status is pending"); a leading one keeps its capital.
    return pre ? pre + word.charAt(0).toLowerCase() + word.slice(1) : word
  })
}

const HOLD_PREFIX: Array<[string, string]> = [
  ['LAB_FAIL_SIBLING', "Lab fail on the farmer's other lot"],
  ['LAB_FAIL', 'Lab fail'],
  ['LAB_LOQ', 'Lab test not sensitive enough'],
  ['RECALL', 'Recall'],
]

/** "LAB_LOQ LR-F7-1" → "Lab test not sensitive enough · LR-F7-1"; free text unchanged. */
export function holdReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null
  const r = reason.trim()
  for (const [prefix, words] of HOLD_PREFIX) {
    if (r === prefix) return words
    if (r.startsWith(`${prefix} `)) return `${words} · ${r.slice(prefix.length + 1).trim()}`
  }
  return r
}

export const DECISION_LABEL: Record<string, string> = {
  RELEASE: 'Released',
  HOLD: 'Held',
  DIVERT: 'Turned ordinary',
  DESTROY: 'Destroyed',
}
export const DECISION_TONE: Record<string, string> = {
  RELEASE: 'RELEASED',
  HOLD: 'HELD',
  DIVERT: 'ORDINARY',
  DESTROY: 'DESTROYED',
}

export const HOLD_SCOPE_LABEL: Record<string, string> = {
  FAMILY: 'Failed lot and what was made from it',
  SIBLING: "Farmer's other lots",
  LOQ: 'Test not sensitive enough',
}

export const PACK_LOT_STATUSES = ['PACKING', 'CLOSED', 'RELEASED', 'WITHDRAWN', 'DESTROYED'] as const
export const PACK_LOT_STATUS_LABEL: Record<string, string> = {
  PACKING: 'Packing',
  CLOSED: 'Closed, waiting for release',
  RELEASED: 'Released',
  WITHDRAWN: 'Withdrawn',
  DESTROYED: 'Destroyed',
}
export const PACK_LOT_HOLD_LABEL: Record<string, string> = { NONE: 'Not on hold', HELD: 'On hold', RECALLED: 'Recalled' }

export const UNIT_TYPE_LABEL: Record<string, string> = { LOT: 'Lot', BATCH: 'Batch', PACK: 'Pack lot', LEFTOVER: 'Left over' }

export const LAB_WARNING_LABEL: Record<string, string> = {
  LAB_NOT_ACCREDITED_ON_TEST_DATE:
    "The lab's accreditation was not valid on the test date, so this report is not accepted for release.",
}

/** In-app link for a tested or held unit by its type. */
export function unitLink(unitType: string | null | undefined, unitId: string): string | null {
  if (unitType === 'LOT') return `/c/lots/${unitId}`
  if (unitType === 'BATCH' || unitType === 'LEFTOVER') return `/c/batches/${unitId}`
  return null
}

/** In-app link for a piece of checklist evidence (null when there is no page for it). */
export function evidenceLink(e: { type: string; id: string; code?: string | null }): string | null {
  switch (e.type) {
    case 'LAB_REPORT':
      return `/c/lab-reports/${e.id}`
    case 'PACK_LOT':
      return `/c/pack-lots/${e.id}`
    case 'EXCEPTION':
      return `/c/exceptions?open=${e.id}`
    case 'RUN':
      return e.code && /-PACK-/.test(e.code) ? `/c/pack-runs/${e.id}` : `/c/runs/${e.id}`
    default:
      return null
  }
}

export const EVIDENCE_LABEL: Record<string, string> = {
  LAB_REPORT: 'Lab report',
  LABEL_VERSION: 'Label',
  PACK_LOT: 'Pack lot',
  RUN: 'Run',
  UNIT: 'Lot or batch',
  EXCEPTION: 'Problem',
}

export const REPORT_STATUS_LABEL: Record<string, string> = {
  ENTERED: 'Waiting for check',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
  SUPERSEDED: 'Replaced',
}
export const OVERALL_LABEL: Record<string, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  INCOMPLETE: 'Incomplete',
  NOT_ACCEPTED: 'Not accepted',
}
export const SAMPLE_KIND_LABEL: Record<string, string> = { BATCH_QC: 'Batch sample', LOT_RETAINED: 'Gate sample' }
